import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Calculator, ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import { calcFunnelPages } from "../data/calcFunnelPages";
import { businessInfo } from "../mock";
import { trackCalcEntry, trackEvent, getCalcEntrySource } from "../utils/analytics";
import {
  localBusinessSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const CalcFunnelPage = ({ slug: slugProp } = {}) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = calcFunnelPages[slug];

  useEffect(() => {
    if (!page) return;
    trackEvent("page_view", {
      page_title: page.metaTitle,
      page_location: typeof window !== "undefined" ? window.location.href : "",
      page_path: `/${page.slug}`,
      entry_source: getCalcEntrySource(),
      calculator: page.slug,
    });
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Claim Calculator", path: "/claim-calculator" },
      { name: page.eyebrow, path: `/${page.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        image={`https://claimbeast.com${page.image}`}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs
        items={[
          { name: "Claim Calculator", path: "/claim-calculator" },
          { name: page.eyebrow, path: `/${page.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="relative bg-black overflow-hidden" data-testid="calcfunnel-hero">
        <div className="absolute inset-0">
          <img
            src={page.image}
            alt={`${page.eyebrow} — Florida`}
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-12 w-12 rounded-full bg-[#E11D26]/20 border border-[#E11D26]/50 flex items-center justify-center text-[#E11D26]">
                <Calculator className="h-6 w-6" />
              </span>
              <span className="text-[#E11D26] text-xs sm:text-sm font-bold tracking-[0.25em] uppercase">
                {page.eyebrow}
              </span>
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {page.h1}
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
              {page.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/claim-calculator"
                data-testid="calcfunnel-cta-primary"
                onClick={() =>
                  trackCalcEntry("cta_click", `funnel_${page.slug}_hero`, {
                    cta_type: "hero",
                    calculator: page.slug,
                  })
                }
                className="group inline-flex items-center justify-between gap-6 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <span className="text-sm underline underline-offset-4">
                  {page.ctaLabel}
                </span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{businessInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What the calculator factors in */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.whatTitle}
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {page.whatItems.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Calculator callout — funnels into /claim-calculator */}
      <CalculatorCallout
        eyebrow={`${page.eyebrow} · 60-Second Estimate`}
        headline={page.calloutHeadline}
        body={page.calloutBody}
        ctaLabel={page.ctaLabel}
        source={`funnel_${page.slug}_callout`}
      />

      {/* How it works */}
      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight max-w-3xl"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.stepsTitle}
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {page.steps.map((step) => (
              <div key={step.n} className="bg-[#0F0F0F] p-6 sm:p-7">
                <div
                  className="text-[#E11D26] text-4xl font-black leading-none"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {step.n}
                </div>
                <h3
                  className="mt-4 text-white font-black uppercase text-base tracking-wide leading-tight"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-[#0A0A0A] border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
            {page.eyebrow} · FAQ
          </div>
          <h2
            className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Common <span className="text-[#E11D26]">Questions</span>
          </h2>
          <div className="mt-9 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details
                key={i}
                className="group py-5"
                data-testid={`calcfunnel-faq-${i}`}
              >
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-14 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Related Florida Claim Help
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {page.related.map((r) => (
              <Link
                key={r.href}
                to={r.href}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {r.text}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Run your numbers — it takes 60 seconds.
          </h3>
          <Link
            to="/claim-calculator"
            data-testid="calcfunnel-cta-final"
            onClick={() =>
              trackCalcEntry("cta_click", `funnel_${page.slug}_final`, {
                cta_type: "final",
                calculator: page.slug,
              })
            }
            className="group inline-flex items-center gap-3 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            <span className="text-sm">{page.ctaLabel}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CalcFunnelPage;
